import { InputManager } from '../../core/input/InputManager'

/**
 * Start screen overlay — shown on mobile to request accelerometer
 * permission. iOS requires the permission call to be in the DIRECT
 * call stack of a user gesture (no async wrappers).
 */

export class StartScreen {
  private overlay: HTMLDivElement

  constructor() {
    this.overlay = document.createElement('div')
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.7); display: flex; justify-content: center;
      align-items: center; z-index: 200; backdrop-filter: blur(8px);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      pointer-events: auto; touch-action: auto;
    `
    this.overlay.innerHTML = `
      <div style="
        text-align: center; color: white; max-width: 350px; padding: 40px;
      ">
        <div style="font-size: 48px; font-weight: 800; margin-bottom: 8px;">
          Heavenly
        </div>
        <div style="font-size: 18px; color: rgba(255,255,255,0.7); margin-bottom: 40px;">
          Ski Game
        </div>
        <button id="start-button" style="
          background: #1a5fb4; color: white; padding: 18px 40px;
          border-radius: 12px; font-size: 20px; font-weight: 700;
          cursor: pointer; display: inline-block; border: none;
          box-shadow: 0 4px 20px rgba(26,95,180,0.4);
          -webkit-tap-highlight-color: rgba(255,255,255,0.2);
          touch-action: manipulation;
          pointer-events: auto;
        ">
          Tap to Play
        </button>
        <div style="
          margin-top: 24px; font-size: 13px; color: rgba(255,255,255,0.5);
          line-height: 1.5;
        ">
          Tilt to steer &bull; Lean forward to tuck<br>
          Lean back to brake &bull; Tap to hop
        </div>
      </div>
    `
    document.body.appendChild(this.overlay)
  }

  /**
   * Wait for the user to tap "Play", request accelerometer permission,
   * then hide the overlay and resolve.
   *
   * IMPORTANT: On iOS, DeviceOrientationEvent.requestPermission() MUST
   * be called synchronously in the user gesture call stack. No async
   * wrappers between the touch event and the permission call.
   */
  waitForStart(input: InputManager): Promise<void> {
    return new Promise((resolve) => {
      const btn = document.getElementById('start-button')!

      const handler = () => {
        // Call requestPermission SYNCHRONOUSLY in the gesture handler
        const DOE = DeviceOrientationEvent as any
        if (typeof DOE.requestPermission === 'function') {
          // iOS 13+ path — must be called directly from user gesture
          DOE.requestPermission()
            .then((permission: string) => {
              if (permission === 'granted') {
                input.setupAccelerometerPublic()
                this.overlay.style.display = 'none'
                resolve()
              } else {
                btn.textContent = 'Permission denied — check Settings > Safari'
                btn.style.background = '#cc3333'
              }
            })
            .catch(() => {
              // Fallback: try without permission (Android, older iOS)
              input.setupAccelerometerPublic()
              this.overlay.style.display = 'none'
              resolve()
            })
        } else {
          // Non-iOS — no permission needed
          input.setupAccelerometerPublic()
          this.overlay.style.display = 'none'
          resolve()
        }
      }

      // Both click and touchend — covers all iOS quirks
      btn.addEventListener('click', handler)
      btn.addEventListener('touchend', (e) => {
        e.preventDefault()
        handler()
      })
    })
  }
}
